import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, throwError } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { Logger } from '@core/interfaces/Logger';
import { WalletRepository } from '@features/wallet/domain/repository/wallet.repository';
import { Wallet } from '@features/wallet/domain/model/wallet.model';
import { WalletMapper } from '@features/wallet/infrastructure/mappers/wallet.mapper';
import { ORGANIZATION_REPOSITORY } from '@shared/domain/tokens/organization.token';
import { SERVICE_CONFIG } from '@shared/domain/tokens/service-config.token';
import { WALLET_STORE_STORAGE } from '../tokens/wallet-store-storage.token';
import { WalletGateway } from '../gateway/wallet.gateway';
import { ApiResponse } from '@features/wallet/infrastructure/dtos/wallet.api.dto';

@Injectable()
export class HttpWalletAdapter implements WalletRepository {
  private readonly walletMapper = inject(WalletMapper);
  private readonly serviceConfig = inject(SERVICE_CONFIG);
  private readonly walletStorage = inject(WALLET_STORE_STORAGE);
  private readonly organizationRepo = inject(ORGANIZATION_REPOSITORY);
  private readonly walletGateway = inject(WalletGateway);
  private readonly logger = inject(Logger);

  getWallet(qrCode: string): Observable<Wallet | null> {
    return this.getPaymentContext().pipe(
      switchMap(context =>
        this.walletGateway.getWallet({ ...context, code: qrCode })
      ),
      map(response => this.mapToDomain(response)),
      catchError(error => this.handleError(error))
    );
  }

  removeWallet(): Observable<void> {
    return this.walletStorage.remove();
  }

  private getPaymentContext(): Observable<{ eventId: string; organizationId: string }> {
    return combineLatest([
      this.organizationRepo.getCurrentOrganization(),
      this.serviceConfig.getCurrentService(),
    ]).pipe(
      first(),
      map(([organization, service]) => {
        if (!organization || !service) {
          throw new Error('Missing organization or service context');
        }

        return {
          eventId: service.evento_id ?? '',
          organizationId: organization._id,
        };
      })
    );
  }

  private mapToDomain(response: ApiResponse): Wallet | null {
    return response?.wallet
      ? this.walletMapper.fromApiToDomain(response.wallet)
      : null;
  }

  private handleError(error: Error): Observable<never> {
    this.logger.error(error, '[HttpWalletAdapter] Error fetching wallet');

    return throwError(() => error);
  }
}
